import type { Invoice as InvoiceRecord } from "$global-providers/rxdb-provider/collections/invoice";
import {
  InvoiceStatus,
  PayerType,
  TransactionStatus,
  TransactionType,
} from "elephant-web/gql";
import {
  calculateInvoice,
  copyInvoiceData,
  findInvoicePayer,
  invoiceMinorUnits,
} from "./calculate-invoice";
import {
  type CalculatedInvoice,
  type InvoiceLinePricing,
  type InvoiceLineSource,
  type InvoiceLineUpdate,
  type InvoicePayerReference,
  type InvoicePayerSource,
  type InvoicePhase,
  type InvoiceSource,
  type InvoiceTransaction,
  InvoiceValidationError,
} from "./invoice-source";

export type FinaliseInvoiceInput = {
  timestamp: string;
  actionedByUserId: string;
  priceListLastSyncedAt: string | null;
  insurerCharges: { payer: InvoicePayerReference; transactionId: string }[];
};

export const invoiceSourceFromRecord = (
  record: InvoiceRecord,
): InvoiceSource => {
  const {
    balance,
    invoiceStatus,
    payers,
    lineItems,
    transactions,
    ...metadata
  } = copyInvoiceData(record);
  const phase: InvoicePhase =
    invoiceStatus === InvoiceStatus.Draft
      ? "DRAFT"
      : invoiceStatus === InvoiceStatus.Pending
        ? "PENDING"
        : invoiceStatus === InvoiceStatus.Void
          ? "VOID"
          : invoiceStatus === InvoiceStatus.Refunded ||
              invoiceStatus === InvoiceStatus.PartiallyRefunded
            ? "REFUNDED"
            : "FINALISED";
  if (
    !Array.isArray(payers) ||
    payers.some((payer) => !Array.isArray(payer.claimedLineItems))
  ) {
    throw new InvoiceValidationError(
      "Complete payer allocation linkage is required",
    );
  }
  const payerSources: InvoicePayerSource[] = payers.map(
    ({ balance: _balance, claimedLineItems: _claims, ...payer }) => payer,
  );
  for (const payer of payers) {
    const claimedIds = payer.claimedLineItems.map((claim) => claim.lineItemId);
    if (
      new Set(claimedIds).size !== claimedIds.length ||
      claimedIds.some((id) => !lineItems.some((line) => line.id === id))
    ) {
      throw new InvoiceValidationError(
        "Claim linkage must identify existing unique line items",
      );
    }
    if (payer.type === PayerType.Patient && claimedIds.length > 0) {
      throw new InvoiceValidationError(
        "Patient payer cannot contain insurer claims",
      );
    }
  }
  return {
    ...metadata,
    currency: balance.currency,
    phase,
    payers: payerSources,
    transactions,
    lineItems: lineItems.map((line) => {
      const owners = payers.flatMap((payer, index) =>
        payer.claimedLineItems.some((claim) => claim.lineItemId === line.id)
          ? [index]
          : [],
      );
      if (owners.length > 1) {
        throw new InvoiceValidationError(
          "V2 line items must have a single payer",
        );
      }
      if (line.quantity == null) {
        throw new InvoiceValidationError("V2 line quantity is required");
      }
      const owner = owners.length === 1 ? payerSources[owners[0]] : undefined;
      return {
        ...line,
        quantity: line.quantity,
        payer: owner
          ? {
              type: PayerType.Insurer,
              name: owner.name,
              ...(owner.id ? { id: owner.id } : {}),
            }
          : { type: PayerType.Patient },
      };
    }),
  };
};

export class Invoice {
  private readonly source: InvoiceSource;
  private readonly calculated: CalculatedInvoice;

  private constructor(source: InvoiceSource) {
    this.source = copyInvoiceData(source);
    this.calculated = calculateInvoice(this.source);
  }

  static from(source: InvoiceSource): Invoice {
    return new Invoice(source);
  }

  static fromRecord(record: InvoiceRecord): Invoice {
    return Invoice.from(invoiceSourceFromRecord(record));
  }

  toSource(): InvoiceSource {
    return copyInvoiceData(this.source);
  }

  toRecord(): CalculatedInvoice {
    return copyInvoiceData(this.calculated);
  }

  addLineItem(line: InvoiceLineSource): Invoice {
    this.assertEditable();
    return Invoice.from({
      ...this.source,
      lineItems: [...this.source.lineItems, line],
    });
  }

  updateLineItem(id: string, update: InvoiceLineUpdate): Invoice {
    this.assertEditable();
    return this.replaceLine(id, (line) => ({ ...line, ...update }));
  }

  updateLinePricing(id: string, pricing: InvoiceLinePricing): Invoice {
    this.assertEditable();
    return this.replaceLine(id, (line) => ({ ...line, ...pricing }));
  }

  removeLineItem(id: string): Invoice {
    this.assertEditable();
    this.findLine(id);
    return Invoice.from({
      ...this.source,
      lineItems: this.source.lineItems.filter((line) => line.id !== id),
    });
  }

  updateInsurance(payers: InvoicePayerSource[]): Invoice {
    this.assertEditable();
    if (payers.some((payer) => payer.type !== PayerType.Insurer)) {
      throw new InvoiceValidationError(
        "Insurance update accepts only insurer payers",
      );
    }
    return Invoice.from({
      ...this.source,
      payers: [
        ...this.source.payers.filter(
          (payer) => payer.type === PayerType.Patient,
        ),
        ...payers,
      ],
    });
  }

  updateEncounter(
    encounter: Pick<InvoiceSource, "patientType" | "inpatientDetails">,
  ): Invoice {
    this.assertEditable();
    return Invoice.from({ ...this.source, ...encounter });
  }

  finalise(input: FinaliseInvoiceInput): Invoice {
    this.assertEditable();
    if (!this.source.lineItems.some((line) => !line.disabled)) {
      throw new InvoiceValidationError(
        "Invoice must have an active line item to finalise",
      );
    }
    if (
      this.source.lineItems.some((line) => !line.disabled && line.missingPrice)
    ) {
      throw new InvoiceValidationError("Invoice has an unpriced line item");
    }
    if (!input.timestamp || !input.actionedByUserId) {
      throw new InvoiceValidationError(
        "Finalisation requires timestamp and actioning user",
      );
    }
    const insurers = this.calculated.payers.filter(
      (payer) => payer.type === PayerType.Insurer && payer.balance.total > 0,
    );
    const indices = input.insurerCharges.map((charge) => {
      if (charge.payer.type !== PayerType.Insurer) {
        throw new InvoiceValidationError(
          "Finalisation charge must target an insurer",
        );
      }
      return findInvoicePayer(this.source.payers, charge.payer);
    });
    if (
      indices.length !== insurers.length ||
      new Set(indices).size !== insurers.length ||
      indices.some((index) => this.calculated.payers[index].balance.total <= 0)
    ) {
      throw new InvoiceValidationError(
        "Finalisation requires one transaction ID for each positive insurer contribution",
      );
    }
    const charges: InvoiceTransaction[] = input.insurerCharges.map(
      (charge, index) => {
        const payer = this.calculated.payers[indices[index]];
        return {
          id: charge.transactionId,
          uuid: null,
          type: TransactionType.ChargeInsurer,
          status: TransactionStatus.Success,
          timestamp: input.timestamp,
          actionedByUserId: input.actionedByUserId,
          amount: {
            currency: this.source.currency,
            value: payer.balance.total,
          },
          payerName: payer.name,
          payerType: payer.type,
          ...(payer.id ? { payerId: payer.id } : {}),
        };
      },
    );
    return Invoice.from({
      ...this.source,
      phase: "FINALISED",
      finalizedAt: input.timestamp,
      finalizedByUserId: input.actionedByUserId,
      priceListLastSyncedAt: input.priceListLastSyncedAt,
      transactions: [...this.source.transactions, ...charges],
    });
  }

  takePayment(
    transactions: InvoiceTransaction[],
    pendingInsurerCharges: FinaliseInvoiceInput["insurerCharges"] = [],
  ): Invoice {
    this.assertPayable();
    if (
      transactions.length === 0 ||
      transactions.some(
        (transaction) =>
          transaction.type !== TransactionType.Payment &&
          transaction.type !== TransactionType.Waiver,
      )
    ) {
      throw new InvoiceValidationError(
        "Payment requires cash or waiver transactions",
      );
    }
    for (const transaction of transactions) {
      this.assertAction(transaction, transaction.type);
      if (
        transaction.type === TransactionType.Payment &&
        !transaction.paymentMethod
      ) {
        throw new InvoiceValidationError("Payment method is required");
      }
    }
    const payable = this.finalisePendingPayment(
      transactions[0],
      pendingInsurerCharges,
    );
    return Invoice.from({
      ...payable.source,
      transactions: [...payable.source.transactions, ...transactions],
    });
  }

  waive(
    transaction: InvoiceTransaction,
    pendingInsurerCharges: FinaliseInvoiceInput["insurerCharges"] = [],
  ): Invoice {
    this.assertAction(transaction, TransactionType.Waiver);
    return this.takePayment([transaction], pendingInsurerCharges);
  }

  exempt(transaction: InvoiceTransaction): Invoice {
    this.assertPayable();
    this.assertAction(transaction, TransactionType.Exempt);
    if (!transaction.exemptionType) {
      throw new InvoiceValidationError("Exemption type is required");
    }
    if (this.source.payers.some((payer) => payer.type !== PayerType.Patient)) {
      throw new InvoiceValidationError(
        "Only patient-only invoices may be exempted",
      );
    }
    const payable = this.finalisePendingPayment(transaction, []);
    return Invoice.from({
      ...payable.source,
      transactions: [...payable.source.transactions, transaction],
    });
  }

  refund(input: {
    transaction: InvoiceTransaction;
    voidTransaction?: InvoiceTransaction;
  }): Invoice {
    if (this.source.phase !== "FINALISED" && this.source.phase !== "REFUNDED") {
      throw new InvoiceValidationError(
        "Only finalised or partially refunded invoices may be refunded",
      );
    }
    this.assertAction(input.transaction, TransactionType.Refund);
    if (this.calculated.balance.exempt > 0) {
      throw new InvoiceValidationError("An exempt invoice cannot be refunded");
    }
    if (
      !input.transaction.refundReason ||
      !input.transaction.refundPaymentMethod
    ) {
      throw new InvoiceValidationError(
        "Refund reason and payment method are required",
      );
    }
    const patient =
      this.calculated.payers[
        findInvoicePayer(this.source.payers, { type: PayerType.Patient })
      ];
    if (-input.transaction.amount.value > patient.balance.paid) {
      throw new InvoiceValidationError(
        "Refund cannot exceed remaining patient payments",
      );
    }
    if (patient.balance.due > 0 && !input.voidTransaction) {
      throw new InvoiceValidationError(
        "Refund must include a void action to close outstanding charges",
      );
    }
    if (input.voidTransaction)
      this.assertAction(input.voidTransaction, TransactionType.Void);
    return Invoice.from({
      ...this.source,
      phase: "REFUNDED",
      transactions: [
        ...this.source.transactions,
        input.transaction,
        ...(input.voidTransaction ? [input.voidTransaction] : []),
      ],
    });
  }

  void(transaction: InvoiceTransaction): Invoice {
    if (
      this.source.phase !== "PENDING" &&
      this.source.phase !== "FINALISED" &&
      this.source.phase !== "REFUNDED"
    ) {
      throw new InvoiceValidationError(
        "Only pending or finalised invoices may be voided",
      );
    }
    this.assertAction(transaction, TransactionType.Void);
    return Invoice.from({
      ...this.source,
      phase: "VOID",
      transactions: [...this.source.transactions, transaction],
    });
  }

  private findLine(id: string): InvoiceLineSource {
    const line = this.source.lineItems.find((item) => item.id === id);
    if (!line) throw new InvoiceValidationError("Line item does not exist");
    return line;
  }

  private replaceLine(
    id: string,
    replace: (line: InvoiceLineSource) => InvoiceLineSource,
  ): Invoice {
    this.findLine(id);
    return Invoice.from({
      ...this.source,
      lineItems: this.source.lineItems.map((line) =>
        line.id === id ? replace(line) : line,
      ),
    });
  }

  private assertEditable(): void {
    if (this.source.phase !== "DRAFT" && this.source.phase !== "PENDING") {
      throw new InvoiceValidationError(
        "Only draft or pending invoices may be edited",
      );
    }
  }

  private assertPayable(): void {
    if (
      (this.source.phase !== "FINALISED" && this.source.phase !== "PENDING") ||
      this.calculated.balance.due <= 0
    ) {
      throw new InvoiceValidationError(
        "Invoice is not unpaid or partially paid",
      );
    }
  }

  private finalisePendingPayment(
    transaction: InvoiceTransaction,
    insurerCharges: FinaliseInvoiceInput["insurerCharges"],
  ): Invoice {
    if (this.source.phase !== "PENDING") return this;
    if (!transaction.actionedByUserId) {
      throw new InvoiceValidationError(
        "Finalisation requires an actioning user",
      );
    }
    return this.finalise({
      timestamp: transaction.timestamp,
      actionedByUserId: transaction.actionedByUserId,
      priceListLastSyncedAt: this.source.priceListLastSyncedAt,
      insurerCharges,
    });
  }

  private assertAction(
    transaction: InvoiceTransaction,
    type: Exclude<TransactionType, TransactionType.Void>,
  ): asserts transaction is InvoiceTransaction & {
    amount: NonNullable<InvoiceTransaction["amount"]>;
  };
  private assertAction(
    transaction: InvoiceTransaction,
    type: TransactionType,
  ): void;
  private assertAction(
    transaction: InvoiceTransaction,
    type: TransactionType,
  ): void {
    if (
      transaction.type !== type ||
      transaction.status !== TransactionStatus.Success ||
      !transaction.id ||
      !transaction.timestamp ||
      !transaction.actionedByUserId
    ) {
      throw new InvoiceValidationError(
        "Financial action requires matching type, success, ID, timestamp and actioning user",
      );
    }
    if (type === TransactionType.Void) return;
    if (
      !transaction.amount ||
      transaction.amount.currency !== this.source.currency
    ) {
      throw new InvoiceValidationError(
        "Financial action requires an invoice-currency amount",
      );
    }
    const value =
      type === TransactionType.Refund
        ? -transaction.amount.value
        : transaction.amount.value;
    invoiceMinorUnits(value);
    if (value === 0)
      throw new InvoiceValidationError(
        "Financial action amount must be positive",
      );
  }
}
